'use client';

import { useState } from 'react';

import { useParams, usePathname } from 'next/navigation';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RichText,
  Switch,
  Text,
} from '@/ui';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib';
import { IconAlertCircleFilled, IconCircleCheckFilled, IconSend } from '@tabler/icons-react';
import { RTEContent, WidthOptions } from '@types';
import { useForm } from 'react-hook-form';
import slugify from 'slugify';
import z, { ZodRawShape } from 'zod';

import { generateFieldSchema } from './generate-form-schema';
import { getFieldComponent } from './get-field-component';
import { getFieldId } from './get-field-id';
import { onSubmit } from './on-submit';

export type FieldType = 'text' | 'email' | 'phone' | 'textarea' | 'dropdown' | 'checkbox' | 'radio';
export type Field = {
  fieldType: FieldType;
  label: string;
  fieldId?: string;
  helpText?: string;
  placeholder?: string;
  required?: boolean;
  options?: { label: string; value: string }[];
};

export interface ContactFormProps {
  id?: string;
  formFields: Field[];
  senderName?: string;
  policies: {
    policy: RTEContent;
  }[];
  submitButtonText: string;
  successMessage: RTEContent;
  errorMessage: RTEContent;
  submissionEmail: string;
  blockWidth: WidthOptions;
  formName: string;
  confirmationEmailField?: string;
  confirmationEmailTemplate?: RTEContent;
}

export function ContactForm({
  senderName,
  blockWidth,
  errorMessage,
  formFields,
  policies,
  submissionEmail,
  submitButtonText,
  successMessage,
  formName,
  confirmationEmailField,
  confirmationEmailTemplate,
  id,
}: ContactFormProps) {
  const [parent] = useAutoAnimate();
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const { domain } = useParams() as { domain: string };
  const slug = usePathname();

  const formSchema = z
    .object({})
    .extend(
      formFields?.reduce((acc, field) => {
        return { ...acc, [getFieldId(field)]: generateFieldSchema(field) };
      }, {} as ZodRawShape),
    )
    .extend(
      policies?.reduce((acc, { policy }) => {
        return {
          ...acc,
          [slugify(policy.content, { lower: true, strict: true })]: z.boolean().refine((val) => val === true, {
            message: 'You must agree to this policy',
          }),
        };
      }, {} as ZodRawShape) || {},
    );

  const defaultValues = {
    ...formFields?.reduce(
      (acc, field) => {
        const fieldName = getFieldId(field);

        if (field.fieldType === 'checkbox') {
          acc[fieldName] = [];
        } else {
          acc[fieldName] = '';
        }

        return acc;
      },
      {} as Record<string, string | string[] | boolean>,
    ),
    ...(policies && policies.length > 0
      ? policies.reduce(
          (acc, { policy }) => {
            acc[slugify(policy.content, { lower: true, strict: true })] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        )
      : {}),
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  return (
    <>
      <Form {...form}>
        <form
          id={id}
          ref={parent}
          onSubmit={form.handleSubmit(
            async (values) =>
              await onSubmit({
                values,
                senderName,
                submissionEmail,
                formFields,
                domain,
                formName: formName || slug,
                confirmationEmailField,
                confirmationEmailTemplate,
              })
                .then(() => {
                  setIsSuccessDialogOpen(true);
                  form.reset();
                })
                .catch((err) => {
                  console.error(err);
                  setIsErrorDialogOpen(true);
                }),
            (errors) => console.log(errors),
          )}
          className={cn('flex w-full flex-col gap-5 px-4', {
            'max-w-none!': blockWidth === 'full',
            'max-w-[theme(screens.3xl)]!': blockWidth === 'desktop',
            'max-w-(--breakpoint-2xl)!': blockWidth === 'wide',
            'max-w-(--breakpoint-xl)!': blockWidth === 'standard',
            'max-w-(--breakpoint-lg)!': blockWidth === 'narrow',
            'max-w-(--breakpoint-md)!': blockWidth === 'prose' || !blockWidth,
          })}
        >
          {formFields?.map((fieldConfig) => (
            <FormField
              key={fieldConfig.label}
              control={form.control}
              name={getFieldId(fieldConfig)}
              render={({ field }) => {
                const Field = getFieldComponent({ fieldConfig, field });

                return (
                  <FormItem>
                    <FormLabel>
                      {fieldConfig.label} {fieldConfig.required && '*'}
                    </FormLabel>
                    <FormControl>{Field}</FormControl>
                    {fieldConfig.helpText && (
                      <Text element="span" className="text-muted-foreground text-sm leading-none">
                        {fieldConfig.helpText}
                      </Text>
                    )}
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          ))}

          {policies?.length > 0 && (
            <div key={'marketing'} className="border-border flex flex-col gap-5 rounded-lg border p-4 py-4">
              {policies.map(({ policy }) => (
                <FormField
                  key={policy.content}
                  control={form.control}
                  name={slugify(policy.content, { lower: true, strict: true })}
                  render={({ field }) => (
                    <>
                      <FormItem className="flex flex-row items-center justify-between space-y-0">
                        <FormLabel className="grow">
                          <RichText>{policy.content}</RichText>
                        </FormLabel>
                        <FormControl>
                          <Switch
                            {...field}
                            value={String(field.value)}
                            checked={field.value as boolean}
                            onCheckedChange={(value) => field.onChange(value)}
                          />
                        </FormControl>
                      </FormItem>
                      <FormMessage />
                    </>
                  )}
                />
              ))}
            </div>
          )}

          <Button size="lg" className="mt-2 w-max gap-3 place-self-center" type="submit">
            {submitButtonText || 'Submit'} <IconSend className="size-4" />
          </Button>
        </form>
      </Form>

      <Dialog open={isSuccessDialogOpen} onOpenChange={setIsSuccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <IconCircleCheckFilled className="text-success size-6" /> Success
            </DialogTitle>
            <DialogDescription asChild>
              <RichText>
                {successMessage?.content || 'Thank you for your submission, we will be in touch shortly.'}
              </RichText>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <IconAlertCircleFilled className="text-destructive size-6" /> Error
            </DialogTitle>
            <DialogDescription asChild>
              <RichText>
                {errorMessage?.content || 'There was an error with your submission, please try again.'}
              </RichText>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
